import React, { useState } from 'react';
import axios from 'axios';
import { uploadImage, registerDriver } from '../../../services/api';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormLabel,
  FormControl,
  Input,
  Paper,
  Divider,
} from '@mui/material';

const MAX_FILE_SIZE_MB = 2;

const DriverRegistration = () => {
  const [formData, setFormData] = useState({
    name: '',
    contactNumber: '',
    email: '',
    password: '',
    licenseNumber: '',
    vehicleNumber: '',
    userPhotoUrl: '',
    licenseImageUrl: '',
    rcImageUrl: '',
    aadharCardUrl: '',
  });

  const [files, setFiles] = useState({
    userPhoto: null,
    licenseImage: null,
    rcImage: null,
    aadharCard: null,
  });

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const uploadFile = async (file) => {
    const res = await uploadImage(file); // Assumes uploadImage returns { data: url }
    return res.data;
  };

  const handleChange = async (e) => {
    const { name, value, files: selectedFiles } = e.target;

    if (selectedFiles) {
      const file = selectedFiles[0];
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setMessage(`File "${name}" exceeds 2MB limit.`);
        return;
      }

      setFiles((prev) => ({ ...prev, [name]: file }));

      try {
        const uploadedUrl = await uploadFile(file);
        const urlField = name + 'Url'; // e.g., 'userPhotoUrl'
        setFormData((prev) => ({ ...prev, [urlField]: uploadedUrl }));
        setMessage(`${name} uploaded successfully.`);
      } catch (err) {
        setMessage(`Upload failed for ${name}: ${err.response?.data?.message || err.message}`);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    try {
      await registerDriver(formData);

      setMessage('Driver registered successfully!');
      setFormData({
        name: '',
        contactNumber: '',
        email: '',
        password: '',
        licenseNumber: '',
        vehicleNumber: '',
        userPhotoUrl: '',
        licenseImageUrl: '',
        rcImageUrl: '',
        aadharCardUrl: '',
      });
      setFiles({
        userPhoto: null,
        licenseImage: null,
        rcImage: null,
        aadharCard: null,
      });
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 5, px: 2 }}>
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h4" gutterBottom>
          Driver Registration
        </Typography>

        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Fill out the details below to complete your driver registration.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                name="name"
                fullWidth
                required
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Number"
                name="contactNumber"
                fullWidth
                required
                value={formData.contactNumber}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                name="email"
                type="email"
                fullWidth
                required
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                name="password"
                type="password"
                fullWidth
                required
                value={formData.password}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom>
            Vehicle & License Info
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Vehicle Number"
                name="vehicleNumber"
                fullWidth
                required
                value={formData.vehicleNumber}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="License Number"
                name="licenseNumber"
                fullWidth
                required
                value={formData.licenseNumber}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom>
            Upload Documents (Max 2MB Each)
          </Typography>
          <Grid container spacing={3}>
            {[
              { label: 'User Photo', name: 'userPhoto' },
              { label: 'License Image', name: 'licenseImage' },
              { label: 'RC Document', name: 'rcImage' },
              { label: 'Aadhar Card', name: 'aadharCard' },
            ].map(({ label, name }) => (
              <Grid item xs={12} sm={6} key={name}>
                <FormControl fullWidth required>
                  <FormLabel>{label}</FormLabel>
                  <Input
                    type="file"
                    name={name}
                    inputProps={{ accept: 'image/*,.pdf' }}
                    onChange={handleChange}
                  />
                  {formData[name + 'Url'] && (
                    <Typography variant="caption" color="green">
                      Uploaded: {formData[name + 'Url']}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            ))}
          </Grid>

          <Box mt={4}>
            <Button type="submit" variant="contained" color="primary" size="large" fullWidth disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </Box>

          {message && (
            <Box mt={2}>
              <Typography color={message.includes('success') ? 'green' : 'error'}>
                {message}
              </Typography>
            </Box>
          )}
        </form>
      </Paper>
    </Box>
  );
};

export default DriverRegistration;